import { Button } from '@/components/Button';
import { CardOverview } from '@/components/CardOverview';
import { SelectInput } from '@/components/SelectInput';
import { Text } from '@/components/Text';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCardStore } from '@/stores/useCardStore';
import { useEventStore } from '@/stores/useEventStore';
import { cn } from '@/utils/cn';
import { handleError } from '@/utils/handleError';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaCheckCircle,
  FaLink,
  FaPlus,
  FaStar,
  FaUser,
  FaUserTie,
  FaUsers,
} from 'react-icons/fa';
import { HiSparkles } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { SelectEventCardModal } from './SelectEventCardModal';

export const EventDetailsCard = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();
  const { event, setUserCard, setUserType } = useEventStore();
  const { createCard, showCard, card } = useCardStore();
  const [isSelectCardModalOpen, setIsSelectCardModalOpen] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState<
    'visitor' | 'exhibitor' | 'expert'
  >('visitor');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSelectedCard = async () => {
      if (event?.user_card_slug) {
        await showCard(event.user_card_slug);
      }
    };
    fetchSelectedCard();
  }, [event?.user_card_slug, showCard]);

  const handleCreateCard = async () => {
    if (!user?.id) return;
    try {
      const newCard = await createCard(user.id);
      if (newCard && event) {
        await setUserCard(event.id, user.id, newCard.slug);
      }
    } catch (error) {
      handleError(error, undefined, navigate);
    }
  };

  const handleSelectCard = async (cardSlug: string | null) => {
    if (!user?.id || !event) return;
    await setUserCard(event.id, user.id, cardSlug);
  };

  const handleRemoveCard = async () => {
    await handleSelectCard(null);
  };

  const handleUserTypeChange = async (e: {
    target: {
      name: string;
      value: string | number | boolean | (string | number | boolean)[];
    };
  }) => {
    setSelectedUserType(e.target.value as 'visitor' | 'exhibitor' | 'expert');
    if (user?.id && event?.id) {
      await setUserType(
        event.id,
        user.id,
        e.target.value as 'visitor' | 'exhibitor' | 'expert',
      );
    }
  };

  const userTypeOptions = [
    {
      value: 'expert',
      label: t('common:event.user_types.expert'),
      icon: <FaUserTie className="size-3" />,
      description: t('eventDetails:card.userTypeDescriptions.expert'),
      color: 'blue',
    },
    {
      value: 'visitor',
      label: t('common:event.user_types.visitor'),
      icon: <FaUser className="size-3" />,
      description: t('eventDetails:card.userTypeDescriptions.visitor'),
      color: 'purple',
    },
    {
      value: 'exhibitor',
      label: t('common:event.user_types.exhibitor'),
      icon: <FaUsers className="size-3" />,
      description: t('eventDetails:card.userTypeDescriptions.exhibitor'),
      color: 'green',
    },
    {
      value: 'organizer',
      label: t('common:event.user_types.organizer'),
      icon: <FaUserTie className="size-3" />,
      description: t('eventDetails:card.userTypeDescriptions.organizer'),
      color: 'red',
    },
  ];

  const getUserTypeIcon = (type: string) => {
    switch (type) {
      case 'expert':
        return <FaUserTie className="size-4 text-white" />;
      case 'exhibitor':
        return <FaUsers className="size-4 text-white" />;
      case 'organizer':
        return <FaUserTie className="size-4 text-white" />;
      default:
        return <FaUser className="size-4 text-white" />;
    }
  };

  const getUserTypeGradient = (type: string) => {
    switch (type) {
      case 'expert':
        return 'from-linko-light via-linko-light to-linko-light';
      case 'exhibitor':
        return 'from-emerald-500 via-green-600 to-green-700';
      case 'organizer':
        return 'from-red-500 via-red-600 to-red-700';
      default:
        return 'from-purple-500 via-violet-600 to-purple-700';
    }
  };

  const getUserTypeBackground = (type: string) => {
    switch (type) {
      case 'expert':
        return 'from-blue-50/90 via-blue-100/70 to-indigo-50/90';
      case 'exhibitor':
        return 'from-emerald-50/90 via-green-100/70 to-teal-50/90';
      case 'organizer':
        return 'from-red-50/90 via-red-100/70 to-rose-50/90';
      default:
        return 'from-purple-50/90 via-violet-100/70 to-indigo-50/90';
    }
  };

  const getUserTypeBorder = (type: string) => {
    switch (type) {
      case 'expert':
        return 'border-blue-200/60';
      case 'exhibitor':
        return 'border-emerald-200/60';
      case 'organizer':
        return 'border-red-200/60';
      default:
        return 'border-purple-200/60';
    }
  };

  // Enhanced animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        staggerChildren: 0.12,
        ease: 'easeOut' as const,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: 'easeOut' as const,
      },
    },
  };

  // If a card is already selected
  if (event?.user_card_slug && card) {
    return (
      <motion.div
        className="w-full space-y-5"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Enhanced Success Header */}
        <motion.div
          className="relative overflow-hidden rounded-2xl border border-linko-dark/20 bg-gradient-to-br from-linko-light/20 via-white to-linko-dark/10 p-5 backdrop-blur-sm"
          variants={itemVariants}
        >
          <div className="absolute -right-8 -top-8 size-24 rounded-full bg-gradient-to-br from-linko-light/30 to-linko-dark/20" />
          <div className="absolute -bottom-6 -left-6 size-16 rounded-full bg-gradient-to-tr from-linko-dark/20 to-linko-light/30" />

          <div className="relative space-y-4">
            <div className="flex flex-1 items-center gap-4">
              <div className="rounded-xl bg-gradient-to-br from-linko-dark to-linko-light p-2.5 shadow-md">
                <FaCheckCircle className="size-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="bg-gradient-to-r from-linko-dark to-gray-800 bg-clip-text text-xl font-bold text-transparent">
                  {t('eventDetails:card.selected')}
                </h2>
                <div className="mt-1 flex items-center gap-2">
                  <HiSparkles className="size-3 text-linko-dark" />
                  <Text className="text-sm font-medium text-linko-dark">
                    {t('eventDetails:card.profileConfiguredSuccessfully')}
                  </Text>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                onClick={() => setIsSelectCardModalOpen(true)}
                className="border border-linko-dark/30 bg-white/90 px-4 py-2 text-xs text-linko-dark shadow-sm backdrop-blur-sm transition-all duration-200 hover:border-linko-dark/50 hover:bg-linko-light/10"
              >
                {t('eventDetails:card.modify')}
              </Button>
              <Button
                className="bg-gradient-to-r from-red-500 to-red-600 px-4 py-2 text-xs text-white shadow-md transition-all duration-200 hover:from-red-600 hover:to-red-700"
                onClick={handleRemoveCard}
                disabled={event.status === 'past' || event.status === 'ongoing'}
              >
                {t('eventDetails:card.remove')}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Enhanced User Type Selection */}
        <motion.div
          className={cn(
            `relative  rounded-2xl border bg-gradient-to-br p-5 backdrop-blur-sm`,
            getUserTypeBackground(event?.user_type),
            getUserTypeBorder(event?.user_type),
          )}
          variants={itemVariants}
        >
          <div className="absolute -right-6 -top-6 size-20 rounded-full bg-white/20" />

          <div className="relative space-y-4">
            <div className="flex items-center gap-4">
              <div
                className={`bg-gradient-to-br ${getUserTypeGradient(event?.user_type)} rounded-xl p-2.5 shadow-md`}
              >
                {getUserTypeIcon(event?.user_type)}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900">
                  {t('eventDetails:card.userType')}
                </h3>
                <Text className="text-sm font-medium text-gray-600">
                  {
                    userTypeOptions.find(opt => opt.value === event?.user_type)
                      ?.description
                  }
                </Text>
              </div>
            </div>
            <SelectInput
              name="userType"
              defaultValue={event?.user_type}
              options={userTypeOptions.map(option => ({
                value: option.value,
                label: option.label,
              }))}
              onChange={handleUserTypeChange}
              useFormik={false}
              placeholder={t('eventDetails:card.userType')}
              menuPortalTarget={document.body}
            />
          </div>
        </motion.div>

        {/* Enhanced Card Preview */}
        <motion.div
          className="relative overflow-hidden rounded-2xl border border-gray-200/50 bg-white p-5"
          variants={itemVariants}
        >
          <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-linko-light via-linko-dark to-linko-light" />

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-gradient-to-br from-linko-light to-linko-dark p-2 shadow-md">
                <FaStar className="size-4 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                {t('eventDetails:card.yourBusinessCard')}
              </h3>
            </div>
            <div className="mx-auto max-w-sm">
              <CardOverview
                card={card}
                onModify={() =>
                  navigate(`/${i18n.language}/dashboard/cards/${card.slug}`)
                }
              />
            </div>
          </div>
        </motion.div>

        <SelectEventCardModal
          isOpen={isSelectCardModalOpen}
          onClose={() => setIsSelectCardModalOpen(false)}
          onSelectCard={handleSelectCard}
          onCreateCard={handleCreateCard}
        />
      </motion.div>
    );
  }

  // Enhanced design for no card selected
  return (
    <motion.div
      className="w-full space-y-5"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Enhanced Header */}
      <motion.div
        className="relative overflow-hidden rounded-2xl border border-linko-dark/20 bg-gradient-to-br from-linko-light/20 via-white to-linko-dark/10 p-5 shadow-lg backdrop-blur-sm"
        variants={itemVariants}
      >
        <div className="absolute -right-8 -top-8 size-24 rounded-full bg-gradient-to-br from-linko-light/30 to-linko-dark/20" />

        <div className="relative flex items-center gap-4">
          <div className="rounded-xl bg-gradient-to-br from-linko-dark to-linko-light p-2.5 shadow-md">
            <FaUser className="size-5 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="bg-gradient-to-r from-linko-dark to-gray-800 bg-clip-text text-xl font-bold text-transparent">
              {t('eventDetails:card.title')}
            </h2>
            <div className="mt-1 flex items-center gap-2">
              <HiSparkles className="size-3 text-linko-dark" />
              <Text className="text-sm font-medium text-gray-600">
                {t('eventDetails:card.setupEventProfile')}
              </Text>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Enhanced User Type Selection */}
      <motion.div
        className={cn(
          `relative overflow-hidden rounded-2xl border p-5 shadow-lg backdrop-blur-sm`,
          `bg-gradient-to-br ${getUserTypeBackground(selectedUserType)}`,
          getUserTypeBorder(selectedUserType),
        )}
        variants={itemVariants}
      >
        <div className="absolute -right-6 -top-6 size-20 rounded-full bg-white/20" />
        <div className="absolute -bottom-4 -left-4 size-16 rounded-full bg-white/15" />

        <div className="relative space-y-4">
          <div className="flex items-center gap-4">
            <div
              className={`bg-gradient-to-br ${getUserTypeGradient(selectedUserType)} rounded-xl p-2.5 shadow-md`}
            >
              {getUserTypeIcon(selectedUserType)}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900">
                {t('eventDetails:card.userType')}
              </h3>
              <Text className="text-sm font-medium text-gray-600">
                {
                  userTypeOptions.find(opt => opt.value === selectedUserType)
                    ?.description
                }
              </Text>
            </div>
          </div>
          <SelectInput
            name="userType"
            defaultValue={selectedUserType}
            options={userTypeOptions.map(option => ({
              value: option.value,
              label: option.label,
            }))}
            onChange={handleUserTypeChange}
            useFormik={false}
            placeholder={t('eventDetails:card.userType')}
            menuPortalTarget={document.body}
          />
        </div>
      </motion.div>

      {/* Enhanced Card Selection */}
      <motion.div
        className="relative overflow-hidden rounded-2xl border border-gray-200/50 bg-white p-6 shadow-lg"
        variants={itemVariants}
      >
        <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-linko-light via-linko-dark to-linko-light" />

        <div className="space-y-6">
          {/* Header */}
          <div className="text-center">
            <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-xl bg-gradient-to-br from-linko-light to-linko-dark shadow-md">
              <FaStar className="size-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">
              {t('eventDetails:card.chooseBusinessCard')}
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              {t('eventDetails:card.selectOrCreateCard')}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Create New Card */}
            <button
              onClick={handleCreateCard}
              className="group relative overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/50 p-6 text-center transition-all duration-300 hover:border-linko-dark hover:bg-linko-light/5"
            >
              <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-linko-light to-linko-dark shadow-sm transition-all duration-300 group-hover:shadow-lg">
                <FaPlus className="size-4 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 transition-colors group-hover:text-linko-dark">
                {t('eventDetails:card.createNewCard')}
              </h4>
              <p className="mt-1 text-xs text-gray-600">
                {t('eventDetails:card.designFreshCard')}
              </p>
            </button>

            {/* Select Existing Card */}
            <button
              onClick={() => setIsSelectCardModalOpen(true)}
              className="group relative overflow-hidden rounded-xl border-2 border-gray-200 bg-white p-6 text-center transition-all duration-300 hover:border-linko-dark hover:shadow-md"
            >
              <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-linko-light to-linko-dark shadow-sm transition-all duration-300 group-hover:shadow-lg">
                <FaLink className="size-4 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 transition-colors group-hover:text-linko-dark">
                {t('eventDetails:card.useExistingCard')}
              </h4>
              <p className="mt-1 text-xs text-gray-600">
                {t('eventDetails:card.chooseFromCards')}
              </p>
            </button>
          </div>

          {/* Quick Info */}
          <div className="rounded-lg bg-gray-50 p-4">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-blue-100 p-1">
                <HiSparkles className="size-3 text-linko-light" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-700">
                  {t('eventDetails:card.proTip')}
                </p>
                <p className="text-xs text-gray-600">
                  {t('eventDetails:card.modifyCardAnytime')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      <SelectEventCardModal
        isOpen={isSelectCardModalOpen}
        onClose={() => setIsSelectCardModalOpen(false)}
        onSelectCard={handleSelectCard}
        onCreateCard={handleCreateCard}
      />
    </motion.div>
  );
};
